import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function SubscriptionCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [planName, setPlanName] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('trxref');

      if (!reference) {
        setStatus('failed');
        return;
      }

      try {
        const response = await axios.post(
          `${API_URL}/api/subscriptions/verify?reference=${reference}`,
          {},
          
        );

        if (response.data.status === 'success') {
          setStatus('success');
          setPlanName(response.data.message);
          toast.success('Subscription activated!');
          await refreshUser();
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Subscription verification error:', error);
        setStatus('failed');
      }
    };

    verifyPayment();
  }, [searchParams, refreshUser]);

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            {status === 'verifying' && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
                <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your subscription...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mx-auto mb-6">
                  <Crown className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Subscription Activated!</h2>
                <p className="text-muted-foreground mb-6">
                  {planName || 'Your subscription is now active. Enjoy creating more events!'}
                </p>
                <div className="space-y-3">
                  <Link to="/dashboard/subscription">
                    <Button className="w-full btn-primary" data-testid="view-subscription-btn">
                      View My Subscription
                    </Button>
                  </Link>
                  <Link to="/dashboard/events/create">
                    <Button variant="outline" className="w-full" data-testid="create-event-btn">
                      Create an Event
                    </Button>
                  </Link>
                </div>
              </>
            )}

            {status === 'failed' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. Please try again or contact support if the issue persists.
                </p>
                <div className="space-y-3">
                  <Link to="/dashboard/subscription">
                    <Button className="w-full btn-primary" data-testid="try-again-btn">
                      Try Again
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button variant="outline" className="w-full" data-testid="go-dashboard-btn">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
